/**
 * Dashboard.tsx — the /patients surface (PATIENT-gated).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY: the signed-in patient's home — the current plan (with the renewal
 * countdown: months remaining + next payment date), order history and billing
 * history (held vs captured phases). The two editable addresses moved to
 * /patients/profile (P1C — CTO: profile info was "really poorly organised on
 * the UI"); Profile is reached via the shell rail/drawer now (2026-07-25 —
 * the old "Profile & settings →" header link was dropped). Sign-out lives in
 * the shell's sidebar account block (no inline button here). The h1 is a
 * personalised greeting ("Hi, <first name>") under a "Dashboard" eyebrow.
 * Money is integer PENCE (0 → "price set at launch"); dates are true-UTC
 * formatted for display. Every fetch has a retryable error state (TA-06). POM
 * medication names are fine here (post-decision surface).
 *
 * EMPTY-ACCOUNT HANDLING: a fresh account (no plan, no orders yet) is NOT an
 * error — `getDashboard` may either return the empty `DashboardData` shape
 * (`currentPlan: null, orders: [], bills: []`) or 404 (no dashboard row minted
 * yet); both are treated as the same warm empty state, never the error screen.
 * See `EMPTY_DASHBOARD` below and its use in `load`.
 *
 * EVIDENCE (2026-09-05): whether the patient owes clinical evidence is an
 * INPUT to `dashboardSummary`, not a second opinion beside it. `evidence`
 * (checking / owed / none / unknown) goes in; the hero copy and the
 * `showEvidencePrompt` gate on the upload card come back out of the same call.
 * Read the block on `EvidenceState` in lib/dashboardSummary for the defect this
 * replaced — do not reintroduce a boolean read that gates the card by itself.
 *
 * ⚠️ AND BOTH ARMS BELOW HAVE TO RENDER IT. `DashboardSummaryCard` is the sole
 * renderer of `summary.evidenceNote`, and the EMPTY-ACCOUNT arm does not mount
 * it — so for a while the honest "we could not check" admission was computed
 * correctly and then thrown away, and a patient with a submitted assessment, no
 * order yet and a failing status read saw nothing at all. The empty arm now
 * carries that line itself via `EvidenceNoticeCard` (see the block there for
 * why it is not simply the whole hero); a third arm would owe the same.
 *
 * MOBILE: the card grid carries `.gps-dashboard-grid` (tokens.css forces a
 * single column under ~600px, matching the `.gps-site-header` pattern) so
 * cards are always full-width on a phone; every interactive control here keeps
 * the ≥44px tap-target floor.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../api/client';
import { getWeightPhotoStatus } from '../api/journey';
import { getDashboard, stopFuturePlanMonths } from '../api/orders';
import type { BillSummary, CurrentPlan, DashboardData } from '../api/types';
import { dashboardSummary, planCardPresentation, stopFutureMonthsCopy, type DashboardSummary, type EvidenceState } from '../lib/dashboardSummary';
import { resumeHref } from '../lib/orderResume';
import { displayTreatmentName, planStatusCopy } from '../lib/patientStatusCopy';
import { formatMonthly } from '../lib/format';
import { ButtonArrow, buttonClass, buttonStyle, Card, ErrorState, Help, PageFrame, Title } from '../ui/kit';
import { CardIconHeader, Row } from '../ui/sections';
import { Menu, MenuItem } from '../ui/Menu';
import { IconActivity, IconPlans, IconUsers } from '../ui/icons';
import { OrdersHistoryCard } from '../ui/OrdersHistoryCard';
import { GuidesCard } from './GuidesCard';
import { WeightPhotoCard } from './WeightPhotoCard';
import { PatientStatusBadge } from '../ui/PatientStatusBadge';
import { BillsCard } from '../patient-portal/BillsCard';
import { SkeletonCard } from '../ui/Skeleton';

/** Status-tone → soft hero tint + accent colour for the summary card. A calm
 * BRAND-GREEN for the "in review" / approved states (reassuring, on-brand — not
 * the amber "attention" tone, which is reserved for the status badge); a richer
 * green when confirmed; the danger tint only for a genuine problem. */
const HERO_TINT: Record<string, { bg: string; accent: string; border: string }> = {
  pending: { bg: 'var(--gps-primary-light)', accent: 'var(--gps-green-text)', border: 'var(--gps-primary-tint-strong)' },
  positive: { bg: 'var(--gps-primary-tint-strong)', accent: 'var(--gps-green-text)', border: 'var(--gps-primary)' },
  danger: { bg: 'var(--gps-danger-bg)', accent: 'var(--gps-danger-text)', border: 'var(--gps-danger-strong)' },
  muted: { bg: 'var(--gps-surface-2)', accent: 'var(--gps-muted)', border: 'var(--gps-border)' },
  neutral: { bg: 'var(--gps-surface-2)', accent: 'var(--gps-text)', border: 'var(--gps-border)' },
};

/** The shape a brand-new account's dashboard is treated as (see EMPTY-ACCOUNT HANDLING above). */
const EMPTY_DASHBOARD: DashboardData = { currentPlan: null, orders: [], bills: [] };

/** The signed-in patient's name for the greeting — their COMPLETE name from
 * fullName when captured, else the email local part (capitalised); "there" when
 * identity carries neither. */
function greetingName(me: { fullName?: string | null; email?: string | null } | null): string {
  const full = me?.fullName?.trim();
  if (full) return full;
  const local = me?.email?.split('@')[0]?.trim();
  if (local) return local.charAt(0).toUpperCase() + local.slice(1);
  return 'there';
}

/** The small uppercase eyebrow that sits above a page title. */
function Eyebrow({ children }: { children: string }) {
  return (
    <div style={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--gps-muted)', marginBottom: '2px' }}>
      {children}
    </div>
  );
}

export function Dashboard() {
  const { me } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  /**
   * Wave 11: does the patient still owe the weight photo they deferred in the
   * journey? Still a SEPARATE, non-blocking read — it never touches the
   * dashboard's own loading/error state — but it is no longer a BOOLEAN, and it
   * no longer gates anything on its own.
   *
   * ⚠️ IT WAS A BOOLEAN INITIALISED TO `false`, AND THAT SHIPPED A LIVE DEFECT
   * (2026-09-05). A 500, a timeout or an auth blip left it at `false`, which is
   * the same value as "this patient owes nothing" — so a patient we had emailed
   * asking for a weight photo signed in to a fully reassuring dashboard with no
   * trace anything had been asked. `'unknown'` is now a state of its own, and
   * `'checking'` keeps the hero from printing an all-clear it has not earned yet.
   *
   * The value feeds `dashboardSummary` and NOTHING else: the hero copy and the
   * upload card both come out of that one call, so they cannot disagree.
   */
  const [evidence, setEvidence] = useState<EvidenceState>('checking');

  const load = useCallback(() => {
    setState('loading');
    getDashboard()
      .catch((err: unknown) => {
        // A brand-new account may 404 (no dashboard row minted yet) rather
        // than return the empty shape — either way this is the empty state,
        // not a fetch failure.
        if (err instanceof ApiError && err.status === 404) return EMPTY_DASHBOARD;
        throw err;
      })
      .then((dashboard) => {
        setData(dashboard);
        setState('ready');
      })
      .catch(() => setState('error'));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Quiet refetch (no skeleton flash) — keeps existing data on a transient miss.
  const refetch = useCallback(() => {
    getDashboard()
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) return EMPTY_DASHBOARD;
        throw err;
      })
      .then((dashboard) => setData(dashboard))
      .catch(() => undefined);
  }, []);

  /**
   * Read the evidence status. Still fail-SOFT — a failure never blocks the page
   * or trips the error screen — but no longer fail-SILENT: the failure is
   * carried into the summary as `'unknown'` so the hero can say we could not
   * check, rather than quietly reading as "nothing is owed".
   */
  const loadEvidence = useCallback(() => {
    getWeightPhotoStatus()
      .then((s) => setEvidence(s.photoPending ? 'owed' : 'none'))
      .catch(() => setEvidence('unknown'));
  }, []);

  useEffect(() => {
    loadEvidence();
  }, [loadEvidence]);

  // Refresh when the patient returns to the tab — covers coming back to an
  // already-open dashboard after completing checkout, so a just-placed order
  // appears without a manual hard refresh.
  useEffect(() => {
    // The evidence status is re-read here TOO. It used to fire once on mount and
    // never again, which cost us twice: a patient who uploaded in another tab
    // kept being asked, and a status read that failed on mount stayed failed for
    // the life of the page — so "Please refresh this page" in the unknown-state
    // copy would have been a lie without this line.
    const onReturn = () => {
      refetch();
      loadEvidence();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') onReturn();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onReturn);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onReturn);
    };
  }, [refetch, loadEvidence]);

  if (state === 'loading') {
    // Skeletons matching the plan/orders/bills card grid keep the page height
    // stable while the dashboard loads (E3 #4).
    return (
      <PageFrame bare>
        <div className="gps-dashboard-grid" style={{ display: 'grid', gap: 'var(--gps-s-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
          <SkeletonCard lines={4} label="Loading your dashboard…" />
          <SkeletonCard lines={3} />
          <SkeletonCard lines={3} />
        </div>
      </PageFrame>
    );
  }

  if (state === 'error' || !data) {
    return (
      <PageFrame bare>
        <ErrorState message="Sorry — we could not load your dashboard." onRetry={load} />
      </PageFrame>
    );
  }

  // Defensively check both fields — a 404 (see load, above) and a genuinely
  // empty 200 response both land here as "no plan, no orders".
  const isEmptyAccount = !data.currentPlan && data.orders.length === 0;
  // orderCaseId → medication, so the bills card can label each order's payments.
  const orderNameById = new Map(data.orders.map((o) => [o.orderCaseId, displayTreatmentName(o.medicationName, o.productKey)] as [string, string]));
  // A patient-safe "what happens next" summary derived from the live wire shape
  // AND the evidence state — the top-of-dashboard hierarchy that explains a
  // held/awaiting-approval order ("Your treatment is being reviewed") rather
  // than leaving the patient to decode a muted history row.
  //
  // THIS IS THE ONLY PLACE EITHER ANSWER COMES FROM. The hero's copy and the
  // WeightPhotoCard's presence are two fields of this one object, so the
  // 2026-09-05 defect — "No action is needed right now." printed directly under
  // a card asking for a photo — is no longer expressible from here.
  // (See lib/dashboardSummary.)
  const summary = dashboardSummary(data, evidence);

  // Where the guides banner's "Finish setting up" lands: the SAME continuation
  // route the order rows already offer (`resumeHref` — the signed-in
  // `#resume=` entry for a resumable order), falling back to the journey page.
  // Computed HERE because this component holds the orders; the card invents no
  // second definition of "where does this patient pick up".
  const guidesContinueHref = data.orders.map(resumeHref).find((href): href is string => Boolean(href)) ?? '/weightloss';

  return (
    <PageFrame bare>
      <div>
        <Eyebrow>Dashboard</Eyebrow>
        {/* A personalised greeting is the page h1 — the "Dashboard" eyebrow above
            gives the section context (Profile now lives only in the rail + its
            own section, not a competing header link). */}
        <Title as="h2">Hi, {greetingName(me)}</Title>
      </div>

      {isEmptyAccount ? (
        <div style={{ display: 'grid', gap: 'var(--gps-s-4)', marginTop: 'var(--gps-s-3)' }}>
          {summary.showEvidencePrompt ? <WeightPhotoCard /> : null}
          {/* THE EVIDENCE LINE HAS TO REACH THIS ARM TOO (2026-09-05 review).
              This arm mounts no hero, and DashboardSummaryCard is the only
              renderer of `evidenceNote` — so the honest "we could not check"
              admission used to render as NOTHING on an empty account. See
              EvidenceNoticeCard for why it is this and not the whole hero. */}
          {!summary.showEvidencePrompt && summary.evidenceNote ? (
            <EvidenceNoticeCard note={summary.evidenceNote} step={summary.evidenceStep} />
          ) : null}
          {/* The guides block reaches the EMPTY arm too — a lead who was sent
              content before checkout is exactly who the banner exists for.
              It renders nothing unless the engagement read says there is
              something to show (see guidesModel). */}
          <GuidesCard continueHref={guidesContinueHref} />
          <EmptyAccountState />
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--gps-s-4)', marginTop: 'var(--gps-s-3)' }}>
          {summary.showEvidencePrompt ? <WeightPhotoCard /> : null}
          {/* Full-width status hero, then a wide main column + a narrow side rail. */}
          <DashboardSummaryCard summary={summary} />
          {/* "Your guides" (2026-09-23): full-width under the hero, per the
              DashboardGuides board. Renders nothing until the engagement read
              says there is something to show; its banner appears only when the
              pending step is genuinely the patient's (guidesModel). */}
          <GuidesCard continueHref={guidesContinueHref} />
          <div className="gps-portal-split">
            <div style={{ display: 'grid', gap: 'var(--gps-s-4)', alignContent: 'start' }}>
              <PlanCard plan={data.currentPlan} bills={data.bills} />
              <OrdersHistoryCard orders={data.orders} />
            </div>
            <div style={{ display: 'grid', gap: 'var(--gps-s-4)', alignContent: 'start' }}>
              <BillsCard bills={data.bills} orderNameById={orderNameById} />
              {/* THE DOOR ON A LANE THE SERVER ALREADY HOLDS OPEN (2026-09-06).
                  clinic-service accepts a patient upload while a staff evidence
                  request is outstanding, but the only patient-facing read
                  (`photoPending`) cannot see that request — so a patient asked
                  for evidence by phone or email had nowhere to answer. This is
                  the optional route, and it asserts nothing: the ask itself
                  still mounts above via `showEvidencePrompt`, and the two are
                  mutually exclusive by construction (see dashboardSummary).
                  In the RAIL below billing, deliberately — an optional card
                  that outranked the hero would read as an obligation. */}
              {summary.showEvidenceSelfServe ? <WeightPhotoCard variant="offered" /> : null}
              <SupportCard />
            </div>
          </div>
        </div>
      )}
    </PageFrame>
  );
}

/** A side-rail card giving the patient a clear route to the team (fills the rail
 * below billing; the "contact us" path matches the plan section's copy). */
function SupportCard() {
  return (
    <Card className="gps-card-interactive">
      <CardIconHeader icon={<IconUsers />} title="Need a hand?" />
      <Help style={{ marginBottom: 0 }}>
        Questions about your treatment, plan or delivery? Our team is here to help — get in touch and we&rsquo;ll
        guide you through the next step.
      </Help>
    </Card>
  );
}

/**
 * The top-of-dashboard status summary (full-width) — the "what happens next"
 * hero. Its badge/headline/body come from {@link dashboardSummary}; a held
 * (Route 1) order reads as "being reviewed / authorised, not charged", never a
 * raw enum. Colour is secondary — the label carries the meaning.
 */
function DashboardSummaryCard({ summary }: { summary: DashboardSummary }) {
  const tint = HERO_TINT[summary.tone] ?? HERO_TINT.neutral;
  return (
    // A tone-tinted HERO: soft status colour, a raised icon chip, the headline +
    // badge, the descriptive lines, and the next step in a surfaced callout.
    <Card style={{ gridColumn: '1 / -1', background: tint.bg, border: `1px solid ${tint.border}` }}>
      {/* Icon + headline on one row; on DESKTOP the pill sits top-right here. */}
      <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
        <span
          aria-hidden="true"
          style={{
            display: 'grid',
            placeItems: 'center',
            width: '44px',
            height: '44px',
            flex: 'none',
            borderRadius: 'var(--gps-r-md)',
            background: 'var(--gps-surface)',
            border: '1px solid rgba(19, 35, 27, 0.08)',
            color: tint.accent,
          }}
        >
          <IconActivity />
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <Title as="h2" size="21px">{summary.headline}</Title>
        </div>
        <div className="gps-hero-badge-top" style={{ flex: 'none' }}>
          <PatientStatusBadge status={{ label: summary.badgeLabel, tone: summary.tone }} />
        </div>
      </div>
      {/* Badge (MOBILE only) + descriptive lines flow FULL-WIDTH, flush to the
          card's left edge (not indented under the icon). */}
      <div style={{ marginTop: '14px' }}>
        <div className="gps-hero-badge-inline" style={{ marginBottom: '10px' }}>
          <PatientStatusBadge status={{ label: summary.badgeLabel, tone: summary.tone }} />
        </div>
        <p style={{ fontSize: '16px', lineHeight: 1.5, color: 'var(--gps-text)', margin: 0 }}>{summary.body}</p>
        {/* The evidence line sits ABOVE the payment note and is deliberately NOT
            muted: it is either something we need from the patient or an
            admission that we could not check, and both outrank reassurance
            about a card hold. Muting it would recreate the defect in a quieter
            font. */}
        {summary.evidenceNote ? (
          <p style={{ fontSize: '15px', lineHeight: 1.5, fontWeight: 600, color: 'var(--gps-text)', margin: '8px 0 0' }}>
            {summary.evidenceNote}
          </p>
        ) : null}
        {summary.paymentNote ? (
          <p style={{ fontSize: '14px', lineHeight: 1.5, color: 'var(--gps-muted)', margin: '6px 0 0' }}>{summary.paymentNote}</p>
        ) : null}
      </div>
      {/* Next step — a surfaced callout that lifts off the tint, with a tone dot. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          marginTop: '16px',
          padding: '12px 14px',
          background: 'var(--gps-surface)',
          border: '1px solid rgba(19, 35, 27, 0.06)',
          borderRadius: 'var(--gps-r-md)',
        }}
      >
        <span aria-hidden="true" style={{ width: '8px', height: '8px', borderRadius: 'var(--gps-r-pill)', background: tint.accent, flex: 'none' }} />
        <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--gps-text)' }}>{summary.nextStep}</span>
      </div>
    </Card>
  );
}

/**
 * The evidence line ON AN ARM THAT RENDERS NO HERO — today, the empty account.
 *
 * ⚠️ WHY THIS EXISTS RATHER THAN JUST MOUNTING `DashboardSummaryCard` HERE.
 * The obvious fix for "the admission never reaches the empty account" is to
 * mount the summary card on both arms. It is the wrong fix: on `kind: 'empty'`
 * that hero renders the headline "No orders yet", the body "Your plan and
 * orders will appear here once you've completed your check." and the next step
 * "Start your 2-minute eligibility check when you're ready." — and
 * `EmptyAccountState`, directly below, already renders the first two VERBATIM
 * and turns the third into the actual button. So it would duplicate the whole
 * empty state, add a "Get started" badge and a tinted status frame to an
 * account that has no status, and put a sentence telling the patient to start a
 * check immediately above the control that starts one.
 *
 * What is genuinely missing from this arm is one thing: the evidence line, the
 * only sentence `EmptyAccountState` cannot say. So that is all this renders.
 *
 * WHEN IT MOUNTS: only when the evidence has something to say that nothing else
 * on the arm is already saying — `evidenceNote` present and the upload card
 * NOT up. That resolves to the failed-read (`'unknown'`) case today, which is
 * the whole point: `'owed'` mounts WeightPhotoCard, whose own heading and
 * Upload control state the ask far better than a line pointing at it could.
 *
 * NOT MUTED, and not danger-toned either — the same judgement the hero makes
 * about this exact string. It is an admission that we could not check, which
 * outranks the surrounding reassurance but is not itself bad news about the
 * patient's treatment. Muting it would recreate the original defect in a
 * quieter font; alarming it would invent a problem we cannot evidence.
 */
function EvidenceNoticeCard({ note, step }: { note: string; step?: string }) {
  return (
    <Card style={{ gridColumn: '1 / -1', background: 'var(--gps-surface-2)', border: '1px solid var(--gps-border)' }}>
      <p style={{ fontSize: '15px', lineHeight: 1.5, fontWeight: 600, color: 'var(--gps-text)', margin: 0 }}>{note}</p>
      {/* The retry AND the route to a human. This is the half that the hero
          gets from `nextStep`; on a non-passive base (which `'empty'` is) that
          slot is not the evidence's, so the step comes from `evidenceStep`. */}
      {step ? (
        <p style={{ fontSize: '14px', lineHeight: 1.5, color: 'var(--gps-text)', margin: '8px 0 0' }}>{step}</p>
      ) : null}
    </Card>
  );
}

/**
 * The fresh-account empty state — replaces the plan/orders/bills cards
 * entirely when there is nothing to show yet.
 */
function EmptyAccountState() {
  return (
    <Card style={{ gridColumn: '1 / -1', textAlign: 'center', padding: 'var(--gps-s-6) var(--gps-s-4)' }}>
      <Title as="h2" size="22px">No orders yet</Title>
      <Help style={{ maxWidth: '28rem', margin: '0 auto var(--gps-s-4)' }}>
        Your plan and orders will appear here once you&rsquo;ve completed your check.
      </Help>
      <div style={{ maxWidth: '20rem', margin: '0 auto' }}>
        <Link to="/weightloss" className={buttonClass('cta')} style={buttonStyle('cta')}>
          Start your 2-minute check
          <ButtonArrow />
        </Link>
      </div>
    </Card>
  );
}

function PlanCard({ plan, bills }: { plan: CurrentPlan | null; bills: BillSummary[] }) {
  if (!plan) {
    // Reached only when there ARE orders but no active plan yet (a truly empty
    // account renders EmptyAccountState instead). The top summary already
    // explains the "being reviewed" state — keep this brief and non-misleading.
    return (
      <Card className="gps-card-interactive">
        <CardIconHeader icon={<IconPlans />} title="Your plan" />
        <Help style={{ marginBottom: 0 }}>Your plan will appear here once a clinician approves your treatment.</Help>
      </Card>
    );
  }
  return <ActivePlanCard plan={plan} bills={bills} />;
}

/**
 * The plan card once there IS a plan — split from `PlanCard` purely so the
 * stop-months state can live in a component that always mounts with a plan.
 * `PlanCard`'s null branch returns before any hook, and the "More" menu needs
 * that state in the card HEADER while the confirm panel renders in the body, so
 * it cannot be owned by a child of both.
 */
function ActivePlanCard({ plan, bills }: { plan: CurrentPlan; bills: BillSummary[] }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Seeded from the server, then owned locally after a successful stop — the
  // dashboard is not re-fetched here, and re-reading a stale `plan` prop would
  // flip the panel back to "not stopped" on the next render.
  const [stopped, setStopped] = useState(plan.cancelAtTermEnd === true);

  const status = planStatusCopy(plan.state);
  const pres = planCardPresentation(plan);
  const terminal = plan.state === 'cancelled' || plan.state === 'completed';
  /* IT RENDERS NOTHING when there is no id (an orders revision that predates the
     dashboard field), when the plan is terminal, or when there are no future
     months to stop. A dead or lying control is worse than no control: the
     patient would press it, get an error, and conclude they cannot leave. */
  const canStop = Boolean(plan.subscriptionId) && !terminal && plan.monthsRemaining > 0 && !stopped;

  async function stop() {
    // Non-null asserted only inside the handler `canStop` already protects.
    const id = plan.subscriptionId as string;
    setBusy(true);
    setError(null);
    try {
      await stopFuturePlanMonths(id);
      setStopped(true);
      setConfirming(false);
    } catch {
      // No server text surfaced: this endpoint's failures are 403/404/500, none
      // of which say anything a patient can act on, and one of them is an
      // ownership refusal we must not narrate.
      setError('Sorry — we could not stop your plan just now. Please try again, or call us.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="gps-card-interactive">
      <CardIconHeader
        icon={<IconPlans />}
        title="Your plan"
        action={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <PatientStatusBadge status={status} />
            {canStop ? <PlanMoreMenu onStop={() => setConfirming(true)} /> : null}
          </span>
        }
      />
      <Row label="Treatment" value={displayTreatmentName(plan.medicationName, plan.productKey)} />
      <Row label="Plan term" value={`${plan.months} month${plan.months === 1 ? '' : 's'}`} />
      <Row label="Price" value={formatMonthly(plan.monthlyPence)} />
      <Row label="Months remaining" value={pres.monthsRemainingValue} />
      <Row label="Next payment" value={pres.nextPaymentValue} />
      {pres.help ? <Help style={{ marginTop: 'var(--gps-s-2)', marginBottom: 0 }}>{pres.help}</Help> : null}
      <StopFutureMonths
        plan={plan}
        bills={bills}
        stopped={stopped}
        confirming={confirming}
        busy={busy}
        error={error}
        onCancelConfirm={() => {
          setConfirming(false);
          setError(null);
        }}
        onConfirm={stop}
      />
    </Card>
  );
}

/**
 * The plan card's three-dot "More" menu.
 *
 * ⚠️ WHY THE STOP MOVED IN HERE (CTO 2026-09-05: the button "is too prominent on
 * the plan card"). It sat in the card body as a full ghost button under the
 * price rows — the only control on a card that is otherwise pure information, so
 * it read as the thing the card was FOR. A patient scanning their own plan met
 * "Stop future months" as the card's call to action.
 *
 * ⚠️ AND IT REINFORCES THE EXISTING RULE RATHER THAN REPLACING IT. The confirm
 * panel below already gives the SAFE choice ("Keep my plan") the primary button
 * and the ending action the quiet ghost, deliberately reversing the usual
 * confirm-dialog reflex. Demoting the entry point to a menu is the same
 * judgement one step earlier; it does not licence making the ending action loud
 * once you are inside. If the panel's button weighting is ever "tidied up", this
 * change was pointless.
 *
 * The menu itself is the estate's `Menu` primitive, so it is a REAL menu — APG
 * menu-button keyboard behaviour (arrows, Home/End, Escape and Tab close and
 * return focus to the trigger), an outside click closes it, and the icon-only
 * trigger carries `aria-label`. Hand-rolling a dropdown here is how the second
 * half of that list gets skipped.
 */
function PlanMoreMenu({ onStop }: { onStop: () => void }) {
  return (
    <Menu
      // The NAME of an icon-only control — without it a screen reader announces
      // "button" and a patient has no idea what the three dots do.
      triggerLabel="Plan options"
      menuLabel="Plan options"
    >
      {({ close }) => (
        <MenuItem
          onSelect={() => {
            close();
            onStop();
          }}
        >
          Stop future months
        </MenuItem>
      )}
    </Menu>
  );
}

/**
 * "Stop future months" — the BODY half of the patient's own way out: the
 * already-stopped note and the confirm panel. The entry point is the card
 * header's "More" menu (see {@link PlanMoreMenu}); the state lives in
 * {@link ActivePlanCard} because the two halves sit either side of the card.
 *
 * WHY IT EXISTS (2026-08-28). Checkout locks every plan to three months and
 * tells the patient, at the moment they commit, that they can stop future
 * months from their account. This control IS that sentence. The capability was
 * already there server-side and owner-scoped; what was missing was anywhere for
 * a patient to press it, which made the checkout promise untrue. The lock and
 * this control shipped together for exactly that reason — if this is removed,
 * the lock in checkout/PlanSelection has to go with it.
 *
 * WHAT IT DOES, PRECISELY: it records cancel-at-term-end intent. It is NOT the
 * ADMIN `cancel-now`, and it is not a refund. ⚠️ IT ALSO DOES NOT STOP THE
 * MONTHS LEFT IN THE COMMITTED TERM — read the docblock over
 * {@link stopFutureMonthsCopy}, which is where every sentence in the panel comes
 * from and where the orders-service behaviour behind each one is written down.
 * The copy is generated there, not typed here, so the two cannot drift.
 *
 * IT ASKS FIRST. One selection on a card the patient is only browsing would end
 * their treatment plan, and the reversal (re-subscribe) is a different, slower
 * flow. The confirm step spells out what happens rather than asking "are you
 * sure", which tells nobody anything.
 */
function StopFutureMonths({
  plan,
  bills,
  stopped,
  confirming,
  busy,
  error,
  onCancelConfirm,
  onConfirm,
}: {
  plan: CurrentPlan;
  /** The billing history — the charge-pending branch is read from it. */
  bills: BillSummary[];
  stopped: boolean;
  confirming: boolean;
  busy: boolean;
  error: string | null;
  onCancelConfirm: () => void;
  onConfirm: () => void;
}) {
  // Derived BEFORE the already-stopped branch, because that note is one of this
  // helper's sentences now — see `alreadyStopped`.
  const copy = stopFutureMonthsCopy(plan, bills);

  if (stopped) {
    return (
      <Help style={{ marginTop: 'var(--gps-s-3)', marginBottom: 0 }}>
        {/* ⚠️ "the months you have already paid for" WAS WRONG HERE TOO — the
            plan ends after its COMMITTED term, and the months of that term left
            to run are still billed. ⚠️ AND SO WAS ITS REPLACEMENT, for two of
            the three states (2026-09-05 review, defect 3): "set to end after its
            committed term" was typed out here, flat, so a DORMANT patient who
            may never be approved and a PAST_DUE patient the dunning ladder may
            cancel next week were both promised a term that runs to the end. It
            comes from `stopFutureMonthsCopy` now, built from the same `ending`
            sentence the confirm panel shows, so the two cannot disagree. */}
        {copy.alreadyStopped}
      </Help>
    );
  }

  if (!confirming) {
    // The trigger lives in the header menu; only a failed attempt leaves
    // anything to say down here, and it must not vanish with the panel.
    return error ? (
      <Help style={{ marginTop: 'var(--gps-s-3)', marginBottom: 0 }}>{error}</Help>
    ) : null;
  }

  return (
    <div
      style={{
        marginTop: 'var(--gps-s-3)',
        padding: 'var(--gps-s-3)',
        background: 'var(--gps-surface-2)',
        border: '1px solid var(--gps-border)',
        borderRadius: 'var(--gps-r-md)',
      }}
    >
      {/* WHAT HAPPENS, NOT "ARE YOU SURE" — and what happens is not the same
          sentence for every patient. Each line is stated BEFORE the buttons,
          never after them. */}
      {copy.chargePending ? (
        <p style={{ margin: '0 0 var(--gps-s-2)', fontSize: '14px', lineHeight: 1.55 }}>{copy.chargePending}</p>
      ) : null}
      <p style={{ margin: '0 0 var(--gps-s-2)', fontSize: '14px', lineHeight: 1.55 }}>{copy.term}</p>
      <p style={{ margin: '0 0 var(--gps-s-2)', fontSize: '14px', lineHeight: 1.55 }}>{copy.ending}</p>
      <p style={{ margin: '0 0 var(--gps-s-3)', fontSize: '14px', lineHeight: 1.55 }}>{copy.refund}</p>
      <div style={{ display: 'flex', gap: 'var(--gps-s-2)', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          className={buttonClass('ghost')}
          style={{ ...buttonStyle('ghost', busy), width: 'auto', minHeight: '44px' }}
        >
          {busy ? 'Stopping…' : 'Yes, stop future months'}
        </button>
        {/* THE SAFE CHOICE IS THE PROMINENT ONE. "Keep my plan" takes the
            primary button and "Yes, stop" the quiet ghost — the reverse of the
            usual confirm-dialog reflex, and deliberate: a patient who opened
            this panel to read it should not find the ending action styled as
            the obvious next press. Moving the ENTRY POINT behind the header's
            "More" menu is the same judgement one step earlier — it reinforces
            this weighting, it does not replace it. */}
        <button
          type="button"
          onClick={onCancelConfirm}
          disabled={busy}
          className={buttonClass('primary')}
          style={{ ...buttonStyle('primary', busy), width: 'auto', minHeight: '44px' }}
        >
          Keep my plan
        </button>
      </div>
      {error ? <Help style={{ marginTop: 'var(--gps-s-2)', marginBottom: 0 }}>{error}</Help> : null}
    </div>
  );
}


// The DeliveryAddressCard and RegisteredAddressCard that used to live here
// moved to src/patients/ProfilePage.tsx (P1C) — addresses are profile
// information, not dashboard content.
